import React, { useEffect, useRef, useState } from 'react';
import { RotateCcw, X } from 'lucide-react';
import { savePracticeSettings } from '../utils/storage';
import {
  PRACTICE_DIFFICULTIES,
  PRACTICE_DURATION_LIMITS,
  getPracticeConfig,
  getPracticeSettingsFields,
  sanitizePracticeValues
} from '../utils/practiceSettings';

const LEVEL_LABELS = { easy: 'Easy', medium: 'Medium', hard: 'Hard' };

const decimalsFor = (step) => (String(step).split('.')[1] || '').length;
const formatNumber = (value, step = 1) => (
  Number.isFinite(value) ? String(Number(value.toFixed(Math.max(1, decimalsFor(step))))) : ''
);
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

export const formatPracticeDuration = (seconds) => {
  const whole = Math.round(Number(seconds) || 0);
  if (whole % 60 === 0) return `${whole / 60} min`;
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
};

// A number box that lets someone type freely ("1.", "") and only hands on
// values that parse; on leaving it, the value is held within its limits.
const NumberField = ({ value, onChange, min, max, step = 1, label, testid, width = 'w-20' }) => {
  const [text, setText] = useState(formatNumber(value, step));
  const editing = useRef(false);

  useEffect(() => {
    if (!editing.current) setText(formatNumber(value, step));
  }, [value, step]);

  return (
    <input
      type="number"
      inputMode="decimal"
      min={min}
      max={max}
      step={step}
      value={text}
      aria-label={label}
      data-testid={testid}
      onFocus={() => { editing.current = true; }}
      onChange={(event) => {
        setText(event.target.value);
        const next = Number(event.target.value);
        if (event.target.value.trim() !== '' && Number.isFinite(next)) onChange(next);
      }}
      onBlur={() => {
        editing.current = false;
        const held = clamp(Number.isFinite(value) ? value : min, min, max);
        if (held !== value) onChange(held);
        setText(formatNumber(held, step));
      }}
      className={`${width} rounded-md border border-[#2b4159] bg-[#060d19] px-2.5 py-1.5 text-right font-mono text-sm text-white focus:border-[#68d9ff] focus:outline-none`}
    />
  );
};

const SettingRow = ({ label, hint, children }) => (
  <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5 border-t border-[#1b2b3f] py-3 first:border-t-0">
    <div className="min-w-0">
      <div className="text-sm font-semibold text-[#edf5ff]">{label}</div>
      {hint && <div className="mt-0.5 text-[11px] text-[#60758d]">{hint}</div>}
    </div>
    <div className="flex items-center gap-1.5 text-xs text-[#8fa5bd]">{children}</div>
  </div>
);

const pickValues = (config, fields) => {
  if (!config) return {};
  const values = { testDuration: config.testDuration };
  if (fields.questions) values.questionCount = config.questionCount;
  fields.extras.forEach(({ key }) => { values[key] = config[key]; });
  return values;
};

const differsFromStandard = (values, standard) => Object.entries(values)
  .some(([key, value]) => Number.isFinite(value) && value !== standard?.[key]);

// The gear's settings on a module's setup screen. Changes are kept per
// difficulty and only ever apply to practice runs.
const PracticeSettingsDialog = ({ title, settingsKey, moduleSettings, initialDifficulty, onClose, onSaved }) => {
  const fields = getPracticeSettingsFields(settingsKey);
  const [level, setLevel] = useState(PRACTICE_DIFFICULTIES.includes(initialDifficulty) ? initialDifficulty : 'medium');
  const [draft, setDraft] = useState(() => Object.fromEntries(PRACTICE_DIFFICULTIES.map((tier) => [
    tier,
    pickValues(getPracticeConfig(moduleSettings, tier), fields)
  ])));
  const dialogRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    dialogRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onCloseRef.current();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const standard = pickValues(moduleSettings?.[level], fields);
  const values = draft[level] || {};
  const update = (changes) => setDraft((current) => ({ ...current, [level]: { ...current[level], ...changes } }));

  const duration = values.testDuration;
  const minutes = Number.isFinite(duration) ? Math.floor(duration / 60) : 0;
  const seconds = Number.isFinite(duration) ? duration % 60 : 0;
  const perQuestion = fields.questions && values.questionCount > 0 ? duration / values.questionCount : NaN;
  const setDuration = (next) => update({ testDuration: Math.round(next) });
  const holdDuration = () => {
    if (!Number.isFinite(duration)) return;
    const held = clamp(duration, PRACTICE_DURATION_LIMITS.min, PRACTICE_DURATION_LIMITS.max);
    if (held !== duration) setDuration(held);
  };

  const save = () => {
    PRACTICE_DIFFICULTIES.forEach((tier) => {
      const clean = sanitizePracticeValues(settingsKey, draft[tier]) || {};
      const tierStandard = moduleSettings?.[tier] || {};
      const changes = Object.fromEntries(Object.entries(clean).filter(([key, value]) => value !== tierStandard[key]));
      savePracticeSettings(settingsKey, tier, Object.keys(changes).length ? changes : null);
    });
    onSaved();
  };

  const moduleName = String(title || '').replace(/\s*-\s*setup$/i, '');

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-[#02050c]/80 p-4 sm:items-center"
      onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="practice-settings-title"
        tabIndex={-1}
        data-testid="practice-settings-dialog"
        className="site-panel flex max-h-[calc(100vh-32px)] w-[520px] max-w-full flex-col overflow-hidden text-white focus:outline-none"
      >
        <div className="site-panel__header">
          <span id="practice-settings-title">Practice settings</span>
          <button type="button" className="site-icon-button h-7 w-7" aria-label="Close" data-testid="practice-settings-close" onClick={onClose}>
            <X size={14} aria-hidden="true" />
          </button>
        </div>
        <div className="overflow-y-auto px-5 py-5 sm:px-6">
          <h2 className="font-['IBM_Plex_Sans_Condensed'] text-xl font-semibold tracking-tight">{moduleName}</h2>
          <p className="mt-1.5 text-xs leading-relaxed text-[#8fa5bd]">
            These only change practice runs. Assessments always use the standard settings, so their scores stay comparable.
          </p>

          <div className="mt-4 grid grid-cols-3 gap-2" role="tablist" aria-label="Difficulty">
            {PRACTICE_DIFFICULTIES.map((tier) => {
              const changed = differsFromStandard(draft[tier] || {}, pickValues(moduleSettings?.[tier], fields));
              return (
                <button
                  key={tier}
                  type="button"
                  role="tab"
                  aria-selected={level === tier}
                  data-testid={`practice-settings-tab-${tier}`}
                  onClick={() => setLevel(tier)}
                  className={`flex min-h-[38px] items-center justify-center gap-1.5 rounded-md border px-3 text-xs font-semibold uppercase tracking-wider transition-colors ${
                    level === tier
                      ? 'border-[#7798ff] bg-[#1a2c55] text-white'
                      : 'border-[#2b4159] bg-[#0a1527] text-[#8fa5bd] hover:border-[#49647f] hover:text-white'
                  }`}
                >
                  {LEVEL_LABELS[tier]}
                  {changed && (
                    <>
                      <span className="h-1.5 w-1.5 rounded-full bg-[#68d9ff]" aria-hidden="true" />
                      <span className="sr-only">(changed)</span>
                    </>
                  )}
                </button>
              );
            })}
          </div>

          <div className="mt-4 rounded-md border border-[#2b4159] bg-[#0a1527] px-4">
            {fields.questions && (
              <SettingRow label="Questions" hint={`Standard ${standard.questionCount} · up to ${fields.maxQuestions}`}>
                <NumberField
                  value={values.questionCount}
                  min={1}
                  max={fields.maxQuestions}
                  label="Questions"
                  testid="practice-settings-questions"
                  onChange={(next) => update({ questionCount: Math.round(next) })}
                />
              </SettingRow>
            )}
            <SettingRow
              label="Total time"
              hint={`Standard ${formatPracticeDuration(standard.testDuration)} · 30 sec to 60 min`}
            >
              <div className="flex items-center gap-1.5" onBlur={holdDuration}>
                <NumberField
                  value={minutes}
                  min={0}
                  max={60}
                  label="Minutes"
                  testid="practice-settings-minutes"
                  width="w-16"
                  onChange={(next) => setDuration(Math.round(next) * 60 + seconds)}
                />
                <span>min</span>
                <NumberField
                  value={seconds}
                  min={0}
                  max={59}
                  label="Seconds"
                  testid="practice-settings-seconds"
                  width="w-16"
                  onChange={(next) => setDuration(minutes * 60 + Math.round(next))}
                />
                <span>sec</span>
              </div>
            </SettingRow>
            {fields.questions && (
              <SettingRow
                label="Time per question"
                hint={standard.questionCount ? `Standard ${formatNumber(standard.testDuration / standard.questionCount, 0.1)} sec` : null}
              >
                <div className="flex items-center gap-1.5" onBlur={holdDuration}>
                  <NumberField
                    value={perQuestion}
                    min={1}
                    max={600}
                    step={0.1}
                    label="Seconds per question"
                    testid="practice-settings-per-question"
                    onChange={(next) => setDuration(next * values.questionCount)}
                  />
                  <span>sec</span>
                </div>
              </SettingRow>
            )}
            {fields.extras.map((extra) => (
              <SettingRow key={extra.key} label={extra.label} hint={`Standard ${formatNumber(standard[extra.key], extra.step)} ${extra.unit}`}>
                <NumberField
                  value={values[extra.key]}
                  min={extra.min}
                  max={extra.max}
                  step={extra.step}
                  label={extra.label}
                  testid={`practice-settings-${extra.key}`}
                  onChange={(next) => update({ [extra.key]: next })}
                />
                <span>{extra.unit}</span>
              </SettingRow>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-[#263a50] bg-[#060d19] px-5 py-4 sm:px-6">
          <button
            type="button"
            className="site-button"
            data-testid="practice-settings-reset"
            disabled={!differsFromStandard(values, standard)}
            onClick={() => update(standard)}
          >
            <RotateCcw size={13} aria-hidden="true" />
            Reset {LEVEL_LABELS[level]} to standard
          </button>
          <div className="flex gap-2">
            <button type="button" className="site-button" data-testid="practice-settings-cancel" onClick={onClose}>Cancel</button>
            <button type="button" className="site-button site-button--primary" data-testid="practice-settings-save" onClick={save}>Save</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PracticeSettingsDialog;
